import { Role } from '../enums/role.enum';
import { ExamSection } from '../enums/exams.enum';
import { InstallmentStatus } from '../enums/accounts.enum';

/**
 * Calendar contract (T103, frozen — docs/requirements/12-calendar.md, T102). A single read-only,
 * role-scoped, date-windowed aggregation endpoint: `GET /calendar?from=&to=`. Consumed by database
 * (T105 — the 3 aggregation queries + indexes), backend (T106 — the service) and frontend (T107 —
 * the month grid). openapi.yaml v1.7.0 additive set, folded into the already-cut v1.8.0 file.
 *
 * Design pillars (all pinned by T102, restated here as the binding wire contract):
 *  - Africa/Cairo day-bucketing reusing cairo-date.util.ts EXCLUSIVELY (never UTC toISOString) — the
 *    server emits an already-resolved Cairo `date` bucket on EVERY event so the FE never recomputes a
 *    day boundary (mirrors the dashboard deep-link "server owns the boundary" rule).
 *  - RBAC is a CURATED per-role event-type set (CALENDAR_EVENT_TYPES_BY_ROLE), server-enforced, NOT
 *    a mirror of module read-RBAC and NOT client-hideable. A zero-type role → 403 (DEC-CAL-403).
 *  - Sales-Agent own-scope (meetings on leads assigned to the caller) is enforced at the QUERY layer
 *    via the exact resolveOwnScope pattern in sales.service.ts, never a post-filter, never overridable
 *    by any client param (there is no such param — the window is the only input).
 *  - deepLink is SERVER-OWNED (DEC-CAL-LINK), identical precedent to DashboardStatCard/ActivityItem.
 */

/** The three event sources the calendar aggregates. Discriminator for CalendarEvent. */
export const CALENDAR_EVENT_TYPES = ['exam', 'meeting', 'payment'] as const;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];

/** IANA tz every calendar boundary (window resolution + day bucketing) is computed in. */
export const CALENDAR_TIMEZONE = 'Africa/Cairo';

/**
 * The visible month-grid window is a whole number of Sat..Fri weeks (35 or 42 days). 42 (6 weeks) is
 * the max possible grid; the request span is capped here so no unbounded range can be requested
 * (T102 R-WIN-4). Span is counted INCLUSIVE of both endpoints.
 */
export const CALENDAR_MAX_WINDOW_DAYS = 42;

/**
 * DEC-CAL-RBAC — the pinned role × event-type matrix (T102 §2), as the single source of truth shared
 * by backend (which types to aggregate + return) and frontend (defense-in-depth icon visibility).
 * This is a CURATED schedule view, intentionally TIGHTER than module read-RBAC (T102 R1) — do NOT
 * widen it to match ACCOUNTS_READ_ROLES / EXAM_BOOKINGS_READ_ROLES. To add/remove a role's access,
 * edit exactly one array here (+ T106 branch) — no route/schema change (T102 R2, DEC-CAL1).
 */
export const CALENDAR_EVENT_TYPES_BY_ROLE: Record<Role, CalendarEventType[]> = {
  Admin: ['exam', 'meeting', 'payment'],
  // 2026-08 — sales are the ones who collect money from students, so both sales roles gained
  // `payment`. As with `meeting`, the two roles differ in REACH, not in type: a Sales Manager sees
  // every student's due dates within their branch scope, a Sales Agent only their own students' —
  // see CALENDAR_PAYMENT_UNRESTRICTED_ROLES.
  'Sales Manager': ['meeting', 'payment'],
  'Exam Coordinator': ['exam'],
  'Sales Agent': ['meeting', 'payment'], // both own-scoped — see the two UNRESTRICTED_ROLES lists
  'Accounts Officer': ['payment'],
  'Admissions Officer': ['exam'], // read-only (destination page governs write; calendar grants nothing)
  'Student Affairs Officer': [], // zero types → endpoint returns 403 (DEC-CAL-403)
};

/**
 * Roles allowed to call GET /calendar (any role with ≥1 event type). Student Affairs Officer is
 * absent → RolesGuard/service denies with 403. Derived from the matrix so the two never drift.
 */
export const CALENDAR_ROLES: Role[] = (Object.keys(CALENDAR_EVENT_TYPES_BY_ROLE) as Role[]).filter(
  (role) => CALENDAR_EVENT_TYPES_BY_ROLE[role].length > 0,
);

/**
 * Roles that see EVERY agent's meetings (unrestricted). Any other meeting-eligible role (i.e. Sales
 * Agent) is own-scoped to `leads.assigned_employee_id = caller.id` at the query layer — the exact
 * `resolveOwnScope(user, [...CALENDAR_MEETING_UNRESTRICTED_ROLES])` shape sales.service.ts uses. The
 * scope is NEVER derived from, nor overridable by, any client-supplied parameter (T102 R-SCOPE-3).
 */
export const CALENDAR_MEETING_UNRESTRICTED_ROLES: Role[] = ['Admin', 'Sales Manager'];

/**
 * 2026-08 — sibling of `CALENDAR_MEETING_UNRESTRICTED_ROLES` for the `payment` type: roles that see
 * EVERY student's installment due dates (within their branch scope). Any other payment-eligible
 * role — i.e. Sales Agent — is own-scoped at the query layer to **their own students**: those whose
 * converted lead is CURRENTLY assigned to them (`leads.assigned_employee_id`, joined via the UNIQUE
 * `leads.student_id`).
 *
 * That is deliberately the SAME ownership definition the student-affairs write path uses, one hop
 * further than the meeting rule: meetings own-scope on the lead itself, payments own-scope on the
 * student the lead converted into. A student registered directly through Admissions has no lead
 * row and therefore no owning agent — their due dates appear for Admin / Accounts Officer /
 * Sales Manager only. As with meetings, the scope is NEVER derived from, nor overridable by, any
 * client-supplied parameter (T102 R-SCOPE-3) — the date window is still this endpoint's only input.
 */
export const CALENDAR_PAYMENT_UNRESTRICTED_ROLES: Role[] = [
  'Admin',
  'Accounts Officer',
  'Sales Manager',
];

/**
 * `GET /calendar` query contract. BOTH bounds are REQUIRED (unlike the dashboard's optional period).
 * The FE computes them from the visible grid via cairo-date.util's cairoMonthBounds + cairoWeekBounds
 * (T102 R-WIN-3) and never invents Cairo math. Validation (surfaced as the standard 400
 * VALIDATION_ERROR envelope with field-level `details` — NO new ErrorEnvelope.code):
 *   R1 `from`/`to`  — required, well-formed Cairo `YYYY-MM-DD`        → field `from`/`to`
 *   R2 order        — `from ≤ to`                                     → field `to`
 *   R3 span         — inclusive span ≤ CALENDAR_MAX_WINDOW_DAYS (42)  → field `to`
 */
export interface CalendarQueryParams {
  /** Inclusive lower bound, Cairo `YYYY-MM-DD` (the Saturday on/before the 1st of the target month). */
  from: string;
  /** Inclusive upper bound, Cairo `YYYY-MM-DD` (the Friday on/after the last day of the target month). */
  to: string;
}

/**
 * Fields shared by every calendar event (openapi.yaml components.schemas.CalendarEventBase). `date`
 * is the ALREADY-RESOLVED Africa/Cairo day cell the event belongs in — present on ALL types (incl.
 * meetings, whose Cairo day is derived server-side from `appointment_date_time`) so the FE buckets by
 * a plain string equality and never recomputes a boundary. `title` is the primary display label
 * (student Arabic name / lead fullName — <bdi>-wrapped in UI, mirroring DashboardActivityItem
 * .entityLabel). `deepLink` is SERVER-OWNED (DEC-CAL-LINK): a relative SPA path, no host, no `/api`
 * prefix — every target is guaranteed reachable for every role that can see the chip (T102 §3
 * "Route guard fit").
 */
export interface CalendarEventBase {
  /**
   * Stable id, UNIQUE within a response, prefixed by type to avoid collision across the three
   * bigint/uuid id spaces: `exam:<examBookingId>`, `meeting:<leadId>`, `payment:<installmentId>`.
   */
  id: string;
  type: CalendarEventType;
  /** Africa/Cairo calendar date `YYYY-MM-DD` — the grid day cell. Server-resolved, never UTC. */
  date: string;
  /** Primary display name: student Arabic name (exam/payment) or lead fullName (meeting). */
  title: string;
  /** Relative SPA deep link to the record's detail page. Server-owned. */
  deepLink: string;
}

/**
 * Exam event — source `exam_bookings.exam_date` (date-only) ⋈ students. Included only when the
 * booking is `Booked`/`Completed` (excludes `Not Booked` and `Cancelled` — a stale date on a
 * cancelled booking must not read as a live exam, T102 §3.1) and the student is not soft-deleted.
 * Each `(student, examSection)` is its OWN chip on the same day — never merged. `deepLink` →
 * `/exam-bookings/:studentCode` for every eligible viewer.
 */
export interface CalendarExamEvent extends CalendarEventBase {
  type: 'exam';
  /** No time column exists for exams → rendered as an all-day chip. */
  allDay: true;
  /** Student Code (also encoded in deepLink); the chip's identifying key. */
  studentCode: string;
  /** SAT / ACT / EST I / EST II — shown as a badge beside the name; disambiguates same-day chips. */
  examSection: ExamSection;
}

/**
 * Meeting event — source `leads.appointment_date_time` (DATETIME) ⋈ the assigned agent's name.
 * Shown regardless of lead `status` (historical browsing, T102 §3.2 / OQ-1); excluded when the lead
 * is soft-deleted. Sales-Agent callers receive ONLY their own (own-scope, query-layer). `deepLink`
 * (lead name) → `/leads/:id`; the agent name is PLAIN TEXT for every role incl. Admin, never a link
 * (DEC-CAL2 — no user-detail route exists this pass).
 */
export interface CalendarMeetingEvent extends CalendarEventBase {
  type: 'meeting';
  /** Has a wall-clock time → rendered as a timed chip. */
  allDay: false;
  /**
   * ISO-8601 instant of the appointment. The FE renders the Cairo local time (e.g. `10:00 ص`) from
   * this; `date` above is the same instant's Cairo day bucket (both server-resolved, cannot drift).
   */
  startsAt: string;
  /** Lead id (also encoded in deepLink). */
  leadId: string;
  /** Assigned agent's display name — PLAIN TEXT ONLY, never a link, any role (DEC-CAL2). */
  assignedAgentName: string;
}

/**
 * Payment event — source `installments.due_date` (date-only) ⋈ its `payment_plans` row ⋈ student.
 * Included only when the parent plan is `status='Active'` (DEC-CAL-ACTIVEPLAN — Archived plans are
 * superseded fee agreements, excluded so a due date no longer being collected can't resurface) and
 * the student is not soft-deleted. ALL installment statuses show (paid ones too — it's a due-date
 * schedule, not an outstanding-balance filter). `installmentStatus` uses the SAME read-time
 * derivation as the Accounts module (accounts.mapper.ts#deriveDisplayStatus — Cairo-day comparison,
 * never persisted, so `Overdue` can appear). `deepLink` → `/accounts/:studentCode`.
 */
export interface CalendarPaymentEvent extends CalendarEventBase {
  type: 'payment';
  /** Due dates are date-only → all-day chip. */
  allDay: true;
  /** Student Code (also encoded in deepLink). */
  studentCode: string;
  /** Installment amount, EGP, decimal-as-string (matches installments.amount serialization, e.g. "5000.00"). */
  amount: string;
  /** Read-time-derived display status (Pending | Paid | Overdue | Partially Paid). Same as Accounts. */
  installmentStatus: InstallmentStatus;
}

/** Discriminated union — narrow on `type`. */
export type CalendarEvent = CalendarExamEvent | CalendarMeetingEvent | CalendarPaymentEvent;

/**
 * `GET /calendar` 200 response (openapi.yaml components.schemas.CalendarResponse). `from`/`to` echo
 * the validated inclusive Cairo window; `events` is the caller-role's allowed types only, already
 * own-scoped. Ordering is not semantically significant — the FE sorts within each day cell (timed
 * before/after all-day is T104's visual call). An empty `events` array for a role WITH access is a
 * normal, frequent state (T102 R-EMPTY-1), NOT an error — only a zero-type role is refused (403).
 */
export interface CalendarResponse {
  /** Echo of the validated inclusive Cairo lower bound (`YYYY-MM-DD`). */
  from: string;
  /** Echo of the validated inclusive Cairo upper bound (`YYYY-MM-DD`). */
  to: string;
  /** Always CALENDAR_TIMEZONE ('Africa/Cairo') — the tz all buckets were computed in. */
  timezone: string;
  /** ISO-8601 instant the aggregation ran. */
  generatedAt: string;
  /** The caller-role's allowed, in-window, own-scoped events across the aggregated types. */
  events: CalendarEvent[];
}
